import React, { useContext, useState } from 'react';
import { UserContext } from '../UserContext';
import { Navigate } from 'react-router-dom';
import api from '../api';
import GalleryManager from '../components/GalleryManager';
import MediaKitPanel from '../components/MediaKitPanel';
import RateCardPanel from '../components/RateCard';
import AvailabilityPanel from '../components/AvailabilityPanel';
import { useTranslation } from '../LanguageContext';


// const apiEndpoint = process.env.REACT_APP_API_URL;

/**
 * Panels sit in a balanced two-column flow on desktop, one column on mobile.
 * `space-y-*` cannot space them because it does not cross a column boundary,
 * so each panel carries its own bottom gap and refuses to split mid-panel.
 */
const PANEL = 'break-inside-avoid mb-5';

// The bounds a creator's own monthly price has to fall inside. These mirror
// MIN_SUBSCRIPTION_USD / MAX_SUBSCRIPTION_USD in the backend settings, which is
// what actually enforces them — these exist so the field can say no before a
// round trip does, not as a second opinion.
const MIN_PRICE = 1;
const MAX_PRICE = 5000;

function Creator() {
    const { user, setUser } = useContext(UserContext);
    const { t } = useTranslation();

    const [mediaAccess, setMediaAccess] = useState(user?.media_access ?? false);
    const [subscriberOnlyMessaging, setSubscriberOnlyMessaging] = useState(user?.Subscriber_only_messaging ?? false);
    const [brandOnlyMessaging, setBrandOnlyMessaging] = useState(user?.brand_only_messaging ?? false);
    const [subscriptionEnabled, setSubscriptionEnabled] = useState(user?.enable_subscriptions ?? false);
    const [coffeesEnabled, setCoffeesEnabled] = useState(user?.coffes_enabled ?? false);
    const [error, setError] = useState('');

    // The price is typed rather than picked, so it is held as the raw string
    // while it is being edited — coercing every keystroke to a number makes
    // "1." and a half-deleted field unrepresentable.
    const savedPrice = Number(user?.subscription_price ?? 0);
    const [priceInput, setPriceInput] = useState(savedPrice > 0 ? String(savedPrice) : '');
    const [priceSaving, setPriceSaving] = useState(false);
    const [priceSaved, setPriceSaved] = useState(false);

    if (!user || user.account_type !== 'creator') {
        return <Navigate to="/profile" replace />;
    }

    const priceValue = Math.round((Number(priceInput) || 0) * 100) / 100;
    const priceValid = priceValue >= MIN_PRICE && priceValue <= MAX_PRICE;
    const priceDirty = priceValue !== savedPrice;

    /**
     * Sends only what changed. The backend leaves any field it was not given
     * alone, and sending everything meant a creator with no price yet could not
     * flip an unrelated switch — the price validation rejected the whole call.
     */
    const savePreferences = async (changes) => {
        try {
            const res = await api.post('api/creator-preferences/', changes);
            if (res.status === 200) {
                setError('');
                setUser(prev => ({ ...prev, ...changes }));
                return true;
            }
        } catch (err) {
            const code = err.response?.data?.error;
            setError(
                code === 'price_required' ? t('creator.priceRequired')
                    : code === 'invalid_subscription_price' ? t('creator.priceOutOfRange')
                        : t('creator.saveError'));
            setMediaAccess(user?.media_access ?? false);
            setSubscriberOnlyMessaging(user?.Subscriber_only_messaging ?? false);
            setBrandOnlyMessaging(user?.brand_only_messaging ?? false);
            setSubscriptionEnabled(user?.enable_subscriptions ?? false);
            setCoffeesEnabled(user?.coffes_enabled ?? false);
        }
        return false;
    };

    const handleMediaAccess = () => {
        const newVal = !mediaAccess;
        setMediaAccess(newVal);
        savePreferences({ media_access: newVal });
    };

    const handleSubscriberMessaging = () => {
        const newVal = !subscriberOnlyMessaging;
        setSubscriberOnlyMessaging(newVal);
        savePreferences({ Subscriber_only_messaging: newVal });
    };

    const handleBrandOnlyMessaging = () => {
        const newVal = !brandOnlyMessaging;
        setBrandOnlyMessaging(newVal);
        savePreferences({ brand_only_messaging: newVal });
    };

    const handleSubscriptionEnabled = () => {
        const newVal = !subscriptionEnabled;
        setSubscriptionEnabled(newVal);
        // Turning it on with no price would publish a subscribe button that the
        // payment endpoint then refuses, so the price rides along with it.
        savePreferences(newVal && priceValue >= MIN_PRICE
            ? { enable_subscriptions: true, subscription_price: priceValue }
            : { enable_subscriptions: newVal });
    };

    const handleCoffeesEnabled = () => {
        const newVal = !coffeesEnabled;
        setCoffeesEnabled(newVal);
        savePreferences({ coffes_enabled: newVal });
    };

    const subscribers = user.subs || [];
    const galleryItems = user.gallery || [];

    const savePrice = async () => {
        if (!priceValid || priceSaving) return;
        setPriceSaving(true);
        setPriceSaved(false);
        const ok = await savePreferences({ subscription_price: priceValue });
        setPriceSaving(false);
        if (ok) {
            setPriceSaved(true);
            setTimeout(() => setPriceSaved(false), 2500);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 pb-24">
            <div className="max-w-6xl mx-auto px-4 pt-6">

                {/* Error message — full width, above the columns */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-sm font-medium px-4 py-3 rounded-xl flex items-center gap-2 mb-5">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        {error}
                    </div>
                )}

                {/* Sections balance across two columns on desktop and stack on
                    mobile. Multi-column rather than a fixed grid because the
                    three panels are very different heights — a fixed split left
                    one side towering over the other. Each panel carries its own
                    bottom gap and stays whole, since `space-y-*` does not cross
                    a column boundary. */}
                <div className="columns-1 lg:columns-2 gap-5">

                {/* Section 1: Preferences */}
                <section className={`rounded-2xl border border-[#c0c0c0] p-5 bg-[#fafafa] ${PANEL}`}>
                    <h2 className="text-lg font-bold text-slate-800 mb-5 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                        </svg>
                        {t('creator.preferences')}
                    </h2>

                    <div className="space-y-5">
                        {/* Toggle: Coffees enabled */}
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-slate-700">{t('creator.enableCoffees') || 'Enable Coffees'}</p>
                                <p className="text-xs text-slate-400 mt-0.5">{t('creator.enableCoffeesDesc') || 'Allow your audience to buy you coffees.'}</p>
                            </div>
                            <button
                                onClick={handleCoffeesEnabled}
                                className={`w-11 h-6 rounded-full flex items-center relative flex-shrink-0 transition-colors ${coffeesEnabled ? 'bg-[#8B5A2B]' : 'bg-slate-200'}`}
                            >
                                <div className={`absolute left-0 transform transition-transform duration-300 ${coffeesEnabled ? 'translate-x-[18px]' : 'translate-x-[-2px]'}`}>
                                    <img src="/coffee.png" alt="Coffee" className="h-9 w-7 object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)] pointer-events-none" />
                                </div>
                            </button>
                        </div>

                        <hr className="border-slate-200" />
                        {/* Toggle: Media access */}
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-slate-700">{t('creator.restrictMedia')}</p>
                                <p className="text-xs text-slate-400 mt-0.5">{t('creator.restrictMediaDesc')}</p>
                            </div>
                            <button
                                onClick={handleMediaAccess}
                                className={`w-11 h-6 rounded-full flex items-center transition px-0.5 flex-shrink-0 ${mediaAccess ? 'bg-primary-600' : 'bg-slate-200'}`}
                            >
                                <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform ${mediaAccess ? 'translate-x-5' : 'translate-x-0'}`}></div>
                            </button>
                        </div>

                        <hr className="border-slate-200" />

                        {/* Toggle: Brand-only messaging */}
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-slate-700">{t('creator.brandOnlyMessaging')}</p>
                                <p className="text-xs text-slate-400 mt-0.5">{t('creator.brandOnlyMessagingDesc')}</p>
                            </div>
                            <button
                                onClick={handleBrandOnlyMessaging}
                                className={`w-11 h-6 rounded-full flex items-center transition px-0.5 flex-shrink-0 ${brandOnlyMessaging ? 'bg-primary-600' : 'bg-slate-200'}`}
                            >
                                <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform ${brandOnlyMessaging ? 'translate-x-5' : 'translate-x-0'}`}></div>
                            </button>
                        </div>

                        <hr className="border-slate-200" />

                        {/* Toggle: Subscriber-only messaging */}
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-slate-700">{t('creator.subMessaging')}</p>
                                <p className="text-xs text-slate-400 mt-0.5">{t('creator.subMessagingDesc')}</p>
                            </div>
                            <button
                                onClick={handleSubscriberMessaging}
                                className={`w-11 h-6 rounded-full flex items-center transition px-0.5 flex-shrink-0 ${subscriberOnlyMessaging ? 'bg-primary-600' : 'bg-slate-200'}`}
                            >
                                <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform ${subscriberOnlyMessaging ? 'translate-x-5' : 'translate-x-0'}`}></div>
                            </button>
                        </div>

                        {/* Both gates on at once would otherwise read as "brands
                            must subscribe first" — say plainly that they don't. */}
                        {brandOnlyMessaging && subscriberOnlyMessaging && (
                            <p className="text-[11px] text-primary-700 bg-primary-50 border border-primary-100 rounded-lg px-3 py-2 leading-relaxed">
                                {t('creator.messagingGatesNote')}
                            </p>
                        )}

                        <hr className="border-slate-200" />

                        {/* Toggle: Enable subscriptions */}
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-slate-700">{t('creator.enableSubs')}</p>
                                <p className="text-xs text-slate-400 mt-0.5">{t('creator.enableSubsDesc')}</p>
                            </div>
                            <button
                                onClick={handleSubscriptionEnabled}
                                className={`w-11 h-6 rounded-full flex items-center transition px-0.5 flex-shrink-0 ${subscriptionEnabled ? 'bg-primary-600' : 'bg-slate-200'}`}
                            >
                                <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform ${subscriptionEnabled ? 'translate-x-5' : 'translate-x-0'}`}></div>
                            </button>
                        </div>

                        {/* Subscription price — the creator's own number.
                            There used to be six fixed tiers here, which meant
                            the only prices anyone could charge were the six
                            somebody else had decided on. The field stays usable
                            while subscriptions are off, because setting a price
                            is exactly what you do before switching them on. */}
                        <div>
                            <label className="text-sm font-semibold text-slate-700 mb-1 block" htmlFor="sub-price">
                                {t('creator.subPrice')}
                            </label>
                            <p className="text-xs text-slate-400 mb-3">{t('creator.subPriceDesc')}</p>

                            <div className="flex items-stretch gap-2">
                                <div className="relative flex-1 min-w-0">
                                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xl font-black text-slate-300 pointer-events-none">$</span>
                                    <input
                                        id="sub-price"
                                        type="number"
                                        inputMode="decimal"
                                        min={MIN_PRICE}
                                        max={MAX_PRICE}
                                        step="0.01"
                                        placeholder="9.99"
                                        value={priceInput}
                                        onChange={(e) => setPriceInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') savePrice(); }}
                                        className="w-full text-lg font-black text-slate-800 border border-slate-200 rounded-xl py-2.5 pl-9 pr-16 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    />
                                    <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 pointer-events-none">
                                        {t('creator.perMonth')}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={savePrice}
                                    disabled={!priceValid || !priceDirty || priceSaving}
                                    className="btn-primary px-4 text-sm font-bold flex items-center gap-2 disabled:opacity-40 flex-shrink-0"
                                >
                                    {priceSaving && <span className="spinner-sm" />}
                                    {t('creator.savePrice')}
                                </button>
                            </div>

                            {/* One line under the field, and only one: the rule
                                while it is being typed, the mistake once it is
                                wrong, the confirmation once it is saved. */}
                            {priceInput !== '' && !priceValid ? (
                                <p className="text-[11px] text-red-600 mt-1.5">
                                    {t('creator.priceOutOfRange')}
                                </p>
                            ) : priceSaved ? (
                                <p className="text-[11px] text-emerald-600 font-semibold mt-1.5">
                                    {t('creator.priceSaved')}
                                </p>
                            ) : (
                                <p className="text-[11px] text-slate-400 mt-1.5">
                                    {t('creator.priceRange')
                                        .replace('{min}', `$${MIN_PRICE}`)
                                        .replace('{max}', `$${MAX_PRICE.toLocaleString('en-US')}`)}
                                </p>
                            )}

                            {/* What a subscriber is actually charged. The old
                                tier grid could show a euro figure while the
                                ledger booked dollars; there is one number now. */}
                            {savedPrice >= MIN_PRICE && (
                                <p className="text-[11px] text-slate-500 mt-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 leading-relaxed">
                                    {t('creator.priceLive')
                                        .replace('{price}', `$${savedPrice.toFixed(2)}`)}
                                </p>
                            )}
                        </div>
                    </div>
                </section>

                {/* Section 2: Availability — whether this account is taking
                    work, and what kind. The discovery ranking reads it, so it
                    is what turns the profile from something a brand has to
                    find into something that gets offered. */}
                <AvailabilityPanel className={PANEL} />

                {/* Section 3: Rate card — the answer to a campaign's budget,
                    published so the negotiation does not start in the DMs. */}
                <RateCardPanel className={PANEL} />

                {/* Section 4: Media kit — the page a brand reads instead of
                    asking for a PDF, and the block pinned to every campaign
                    application this account files.

                    It takes the top of the right column on desktop, which is
                    what `break-before-column` pins rather than leaves to the
                    balancer: it is the panel a creator comes here to fill in,
                    and where it lands must not depend on how tall the panels
                    above it happen to be for this account. The break is `lg:`
                    only — forcing one inside the single-column phone layout
                    would push the panel into a second column beside the page. */}
                <MediaKitPanel className={`${PANEL} lg:break-before-column`} />

                {/* Section 5: Subscribers */}
                <section className={`rounded-2xl border border-[#c0c0c0] p-5 bg-[#f9f9fc] ${PANEL}`}>
                    <h2 className="text-lg font-bold text-slate-800 mb-5 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        {t('creator.subscribers')}
                        <span className="text-xs text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full font-medium ml-auto">{subscribers.length} {t('creator.total')}</span>
                    </h2>

                    {subscribers.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-4">{t('creator.noSubs')}</p>
                    ) : (
                        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                            {subscribers.map((sub) => (
                                <div key={sub.id} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-100 hover:border-slate-200 transition">
                                    <img
                                        src={sub.sub_pp}
                                        alt={sub.username}
                                        className="w-10 h-10 rounded-full object-cover ring-2 ring-slate-100 flex-shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-slate-800 truncate">{sub.subscriber_name}</p>
                                        <p className="text-xs text-slate-400">
                                            {t('creator.subscribedOn')} {new Date(sub.subscription.started_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                        </p>
                                    </div>
                                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${sub.subscription.status !== 'active'
                                        ? 'text-red-600 bg-red-50'
                                        : sub.subscription.cancel_at_period_end === true
                                            ? 'text-amber-600 bg-amber-50'
                                            : 'text-emerald-600 bg-emerald-50'
                                        }`}>
                                        {sub.subscription.status !== 'active'
                                            ? t('creator.inactive')
                                            : sub.subscription.cancel_at_period_end === true
                                                ? (() => {
                                                    // eslint-disable-next-line
                                                    const cancelDate = sub.subscription.current_period_end
                                                        ? new Date(sub.subscription.current_period_end).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                                        : sub.subscription.ended_at
                                                            ? new Date(sub.subscription.ended_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                                            : '—';
                                                    return `Canceled ${new Date(sub.subscription.canceled_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
                                                })()
                                                : t('creator.active')
                                        }
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
                {/*! cancelled yazsın ve konuşma yerinde de görünsün  */}
                {    /* Section 5: Gallery */}
                <section className={`rounded-2xl border border-[#c0c0c0] p-5 bg-[#f9f9fc] ${PANEL}`}>
                    <h2 className="text-lg font-bold text-slate-800 mb-5 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {t('creator.gallery')}
                        <span className="text-xs text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full font-medium ml-auto">{galleryItems.length} {t('creator.items')}</span>
                    </h2>

                    <GalleryManager
                        items={galleryItems}
                        onAdded={(item) => setUser(prev => ({ ...prev, gallery: [...(prev.gallery || []), item] }))}
                        onRemoved={(item) => setUser(prev => ({
                            ...prev,
                            gallery: (prev.gallery || []).filter(row => row.url !== item.url),
                        }))}
                        onError={setError}
                    />
                </section>

                </div>

            </div >

        </div >
    );
}

export default Creator;
